import React, { useEffect, useState } from 'react';
import { Mail, X } from 'lucide-react';
import { getThemeColor } from '../../config/appConfig';
import apiService from '../../utils/apiService';

const THEME = getThemeColor('primary');

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== '') return obj[key];
  }
  return undefined;
};

const normalizeCommissions = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.result ?? payload?.data ?? payload?.Result ?? [];
  if (!Array.isArray(list)) return [];

  return list
    .map((row) => {
      const id = pick(row, 'affiliateSubCommissionId', 'AffiliateSubCommissionId', 'id');
      const percentage = Number(pick(row, 'percentage', 'Percentage'));
      const isActive = pick(row, 'isActive', 'IsActive');
      if (!Number.isFinite(percentage)) return null;
      if (isActive === false || isActive === 0 || String(isActive).toLowerCase() === 'false') {
        return null;
      }
      return {
        id: id != null ? String(id) : String(percentage),
        percentage,
        label: `${percentage % 1 === 0 ? percentage.toFixed(0) : percentage}%`,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.percentage - b.percentage);
};

const InviteEmailModal = ({ open, onClose, onSend, sending = false }) => {
  const [email, setEmail] = useState('');
  const [percentage, setPercentage] = useState('');
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    setEmail('');
    setPercentage('');
    setError('');
    setOptions([]);

    let cancelled = false;
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const res = await apiService.getAffiliateSubCommissions();
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || res.message || 'Failed to load commission options');
          return;
        }
        const next = normalizeCommissions(res.data ?? res.result ?? res);
        setOptions(next);
        if (next.length > 0) {
          setPercentage(String(next[0].percentage));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load commission options');
        }
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    };

    loadOptions();

    const onKey = (e) => {
      if (e.key === 'Escape' && !sending) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelled = true;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, sending]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter an email address');
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError('Enter a valid email address');
      return;
    }
    if (percentage === '' || percentage == null) {
      setError('Please select a commission percentage');
      return;
    }
    const ok = await onSend?.(trimmed, Number(percentage));
    if (ok) onClose?.();
  };

  return (
    <div className="raf-modal" role="presentation">
      <button
        type="button"
        className="raf-modal__backdrop"
        onClick={() => !sending && onClose?.()}
        aria-label="Close"
      />
      <div
        className="raf-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="raf-invite-title"
        style={{ '--raf-primary': THEME }}
      >
        <div className="raf-modal__head">
          <div className="raf-modal__title">
            <Mail className="w-5 h-5" aria-hidden="true" />
            <h2 id="raf-invite-title">Invite a sub partner by email</h2>
          </div>
          <button
            type="button"
            className="raf-modal__close"
            onClick={() => !sending && onClose?.()}
            aria-label="Close"
            disabled={sending}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="raf-modal__body">
            <p>
              Enter the sub partner&apos;s email address and choose the commission share. We&apos;ll
              send them a referral invite from your account.
            </p>
            <label className="raf-modal__label" htmlFor="raf-friend-email">
              Sub partner&apos;s email
            </label>
            <input
              id="raf-friend-email"
              type="email"
              className="raf-modal__input"
              placeholder="subpartner@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              autoFocus
              autoComplete="email"
              disabled={sending}
            />

            <label className="raf-modal__label raf-modal__label--spaced" htmlFor="raf-commission">
              Sub commission
            </label>
            <select
              id="raf-commission"
              className="raf-modal__input raf-modal__select"
              value={percentage}
              onChange={(e) => {
                setPercentage(e.target.value);
                if (error) setError('');
              }}
              disabled={sending || loadingOptions || options.length === 0}
            >
              {loadingOptions ? (
                <option value="">Loading…</option>
              ) : options.length === 0 ? (
                <option value="">No options available</option>
              ) : (
                options.map((opt) => (
                  <option key={opt.id} value={String(opt.percentage)}>
                    {opt.label}
                  </option>
                ))
              )}
            </select>

            {error ? <span className="raf-modal__error">{error}</span> : null}
          </div>

          <div className="raf-modal__foot">
            <button
              type="button"
              className="raf-modal__btn raf-modal__btn--ghost"
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="raf-modal__btn raf-modal__btn--primary"
              disabled={sending || loadingOptions || options.length === 0}
            >
              {sending ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteEmailModal;
